/** Этап 5: рассылки, журнал действий, настройки */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast, useTRPC } from '@barev/web';
import { useEffect, useState } from 'react';
import { errText, useT } from '../i18n.tsx';
import { Avatar, Dialog, Field, I, Skeleton } from '../components/kit.tsx';
import { actionLabel } from './Dashboard.tsx';

const LANGS = ['ru', 'en', 'hy', 'zh', 'es'] as const;
type L5 = Record<(typeof LANGS)[number], string>;

// ───── рассылка ─────

/** Рассылка по текущим фильтрам списка клиентов */
export function BroadcastDialog({ filters, onClose }: { filters: Record<string, unknown>; onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const preview = useQuery(trpc.bo.broadcasts.preview.queryOptions(filters as never));
  const [texts, setTexts] = useState<L5>({ ru: '', en: '', hy: '', zh: '', es: '' });
  const [more, setMore] = useState(false);
  const [appButton, setAppButton] = useState(true);
  const [sure, setSure] = useState(false);
  const onError = (e: unknown) => toast(errText(t, e), 'error');
  const test = useMutation(trpc.bo.broadcasts.test.mutationOptions({ onSuccess: () => toast(t('bc.testSent')), onError }));
  const send = useMutation(
    trpc.bo.broadcasts.send.mutationOptions({
      onSuccess: (r) => {
        qc.invalidateQueries({ queryKey: trpc.bo.broadcasts.list.queryKey() });
        toast(t('bc.started', { n: r.total }));
        onClose();
        location.hash = '#/broadcasts';
      },
      onError,
    }),
  );
  const p = preview.data;
  const missing = p ? LANGS.filter((l) => l !== 'ru' && p.byLocale[l] > 0 && !texts[l].trim()) : [];
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={t('bc.title')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" disabled={!texts.ru.trim() || test.isPending} onClick={() => test.mutate({ text: texts.ru, appButton })}>
            {t('bc.test')}
          </button>
          <span className="bo-grow" />
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn bo-btn--accent"
            disabled={!texts.ru.trim() || !p?.total || send.isPending}
            onClick={() => (sure ? send.mutate({ filters: filters as never, texts, appButton }) : setSure(true))}
          >
            {sure ? t('bc.confirm', { n: p?.total ?? 0 }) : t('bc.send', { n: p?.total ?? 0 })}
          </button>
        </>
      }
    >
      <div className="bo-bcwho">
        <I name="clients" size={20} />
        {p ? (
          <span>
            <b>{t('bc.recipients', { n: p.total })}</b>
            <span className="bo-small bo-muted" style={{ display: 'block' }}>
              {LANGS.filter((l) => p.byLocale[l] > 0)
                .map((l) => `${l.toUpperCase()} ${p.byLocale[l]}`)
                .join(' · ') || '—'}
            </span>
          </span>
        ) : (
          <span className="bo-muted">{t('ui.loading')}</span>
        )}
      </div>
      <p className="bo-hint">{t('bc.filtersHint')}</p>
      <Field label={t('bc.textRu')}>
        <textarea className="bo-textarea" style={{ minHeight: 120 }} value={texts.ru} maxLength={3500} onChange={(e) => (setTexts({ ...texts, ru: e.target.value }), setSure(false))} placeholder={t('bc.textPh')} />
      </Field>
      <button type="button" className="bo-side__link" style={{ color: 'var(--bc-accent)', padding: 0, alignSelf: 'flex-start' }} onClick={() => setMore((m) => !m)}>
        {t('bc.otherLangs')} {more ? '▴' : '▾'}
      </button>
      {more &&
        LANGS.filter((l) => l !== 'ru').map((l) => (
          <Field key={l} label={`${l.toUpperCase()}${p?.byLocale[l] ? ` · ${t('bc.clientsN', { n: p.byLocale[l] })}` : ''}`}>
            <textarea className="bo-textarea" style={{ minHeight: 70 }} value={texts[l]} maxLength={3500} onChange={(e) => setTexts({ ...texts, [l]: e.target.value })} />
          </Field>
        ))}
      {missing.length > 0 && <p className="bo-hint">{t('bc.fallback', { langs: missing.map((l) => l.toUpperCase()).join(', ') })}</p>}
      <label className="bo-check">
        <input type="checkbox" checked={appButton} onChange={(e) => setAppButton(e.target.checked)} />
        {t('bc.appButton')}
      </label>
      {sure && <p className="bo-hint" style={{ color: 'var(--bc-danger)' }}>{t('bc.sureHint')}</p>}
    </Dialog>
  );
}

export function Broadcasts() {
  const { t, dateTime } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const qc = useQueryClient();
  const list = useQuery({ ...trpc.bo.broadcasts.list.queryOptions(), refetchInterval: (q) => (q.state.data?.some((b) => b.status === 'SENDING') ? 3000 : false) });
  const cancel = useMutation(trpc.bo.broadcasts.cancel.mutationOptions({ onSuccess: () => qc.invalidateQueries({ queryKey: trpc.bo.broadcasts.list.queryKey() }), onError: (e) => toast(errText(t, e), 'error') }));
  return (
    <>
      <a className="bo-back" href="#/clients">
        <I name="back" size={16} />
        {t('card.back')}
      </a>
      <div className="bo-head">
        <h1 className="bo-title">{t('bc.history')}</h1>
      </div>
      <p className="bo-hint" style={{ marginBottom: 14 }}>
        {t('bc.historyHint')}
      </p>
      {!list.data ? (
        <Skeleton h={200} />
      ) : list.data.length === 0 ? (
        <div className="bo-panel bo-empty">{t('bc.none')}</div>
      ) : (
        <div className="bo-col" style={{ gap: 10 }}>
          {list.data.map((b) => {
            const done = b.sent + b.blocked + b.failed;
            return (
              <section key={b.id} className="bo-panel">
                <div className="bo-panel__head">
                  <span className="bo-small bo-muted">
                    {dateTime(b.createdAt)} · {b.by ?? '—'}
                  </span>
                  <span className={`bo-chip${b.status === 'DONE' ? ' bo-chip--ok' : b.status === 'SENDING' ? ' bo-chip--warn' : ''}`}>{t(`bc.status.${b.status}`)}</span>
                </div>
                <p style={{ margin: '0 0 12px', whiteSpace: 'pre-wrap' }}>{b.text}</p>
                <div className="bo-bar__track" style={{ height: 8 }}>
                  <div className="bo-bar__fill" style={{ width: `${b.total ? (done / b.total) * 100 : 0}%` }} />
                </div>
                <div className="bo-small" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
                  <span>{t('bc.sent', { n: b.sent, total: b.total })}</span>
                  {b.blocked > 0 && <span className="bo-muted">{t('bc.blocked', { n: b.blocked })}</span>}
                  {b.failed > 0 && <span style={{ color: 'var(--bc-danger)' }}>{t('bc.failed', { n: b.failed })}</span>}
                  {b.status === 'SENDING' && (
                    <button type="button" className="bo-side__link" style={{ color: 'var(--bc-danger)', padding: 0 }} onClick={() => confirm(t('bc.cancelConfirm')) && cancel.mutate({ id: b.id })}>
                      {t('bc.cancel')}
                    </button>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

// ───── журнал ─────

/** Куда ведёт запись журнала */
function entityHref(entity: string, id: string | null) {
  if (!id) return null;
  const map: Record<string, string> = { clients: 'clients', orders: 'orders', providers: 'providers', chats: 'chats', crm: 'crm', agency: 'agencies', venue: 'venues' };
  const base = map[entity];
  return base ? `#/${base}/${id}` : null;
}

export function Journal() {
  const { t, dateTime } = useT();
  const trpc = useTRPC();
  const [who, setWho] = useState('');
  const [entity, setEntity] = useState('');
  const [days, setDays] = useState(7);
  const [open, setOpen] = useState<string | null>(null);
  const facets = useQuery(trpc.bo.journal.facets.queryOptions());
  const list = useInfiniteQuery({
    ...trpc.bo.journal.list.infiniteQueryOptions({ adminUserId: who || undefined, entity: entity || undefined, days }, { getNextPageParam: (p) => p.next ?? undefined }),
    placeholderData: keepPreviousData,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <>
      <div className="bo-head">
        <h1 className="bo-title">{t('jr.title')}</h1>
      </div>
      <div className="bo-toolbar">
        <select className="bo-select" style={{ width: 'auto' }} value={who} onChange={(e) => setWho(e.target.value)} aria-label={t('jr.who')}>
          <option value="">{t('jr.everyone')}</option>
          {facets.data?.staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <select className="bo-select" style={{ width: 'auto' }} value={entity} onChange={(e) => setEntity(e.target.value)} aria-label={t('jr.what')}>
          <option value="">{t('jr.allSections')}</option>
          {facets.data?.entities.map((x) => (
            <option key={x} value={x}>
              {t(`jr.entity.${x}`) === `jr.entity.${x}` ? x : t(`jr.entity.${x}`)}
            </option>
          ))}
        </select>
        <div className="bo-pills">
          {[1, 7, 30, 90].map((d) => (
            <button key={d} type="button" className="bo-pill" aria-pressed={days === d} onClick={() => setDays(d)}>
              {t('jr.days', { n: d })}
            </button>
          ))}
        </div>
      </div>
      {list.isPending ? (
        <Skeleton h={300} />
      ) : items.length === 0 ? (
        <div className="bo-panel bo-empty">{t('jr.empty')}</div>
      ) : (
        <section className="bo-panel" style={{ padding: 8 }}>
          <ul className="bo-journal">
            {items.map((r) => {
              const href = entityHref(r.entity, r.entityId);
              return (
                <li key={r.id}>
                  <span className="bo-feed__time">{dateTime(r.at)}</span>
                  {r.by ? <Avatar name={r.by.name} id={r.by.id} size={28} /> : <span style={{ width: 28 }} />}
                  <span className="bo-grow" style={{ minWidth: 0, whiteSpace: 'normal' }}>
                    <b>{r.by?.name ?? t('jr.system')}</b> {actionLabel(t, r.action)}
                    {href && (
                      <>
                        {' '}
                        <a href={href} className="bo-small">
                          {t('jr.open')}
                        </a>
                      </>
                    )}
                    {r.data && (
                      <button type="button" className="bo-side__link" style={{ color: 'var(--bc-ink-3)', padding: '0 0 0 8px', fontSize: 12 }} onClick={() => setOpen(open === r.id ? null : r.id)}>
                        {open === r.id ? t('jr.hide') : t('jr.details')}
                      </button>
                    )}
                    {open === r.id && <pre className="bo-json">{JSON.stringify(r.data, null, 2)}</pre>}
                  </span>
                </li>
              );
            })}
          </ul>
          {list.hasNextPage && (
            <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" style={{ margin: 10 }} disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {t('jr.more')}
            </button>
          )}
        </section>
      )}
    </>
  );
}

// ───── настройки ─────

const GROUPS: { key: string; keys: string[] }[] = [
  { key: 'orders', keys: ['cancel.freeHours', 'offer.acceptTimeoutMinutes', 'assign.auto', 'assign.maxAutoOffers'] },
  { key: 'subs', keys: ['barevPlus.monthlyPrice', 'barevPlus.maxDiscountPct', 'elite.monthlyPrice', 'elite.maxDiscountPct'] },
  // онлайн-оплата Tribute: подписки в €, заказы пересчитываются из ֏ по курсу
  { key: 'online', keys: ['barevPlus.monthlyPriceEur', 'elite.monthlyPriceEur', 'fx.eurAmd', 'fx.eurRub'] },
  // подарок новым клиентам задаётся у каждого агентства; giftDays — только число в текстах лендинга
  { key: 'agencies', keys: ['agency.attributionMonths', 'agency.defaultCommissionPct', 'barevPlus.giftDays'] },
  { key: 'points', keys: ['points.pendingHours', 'points.maxSharePct', 'points.minRedeem', 'points.burnDays', 'points.warnDays1', 'points.warnDays2', 'points.requestTtlHours', 'points.streakGapDays', 'points.diffHighlightPct', 'points.supportCanManage', 'points.opMaxPerOperation', 'points.opMaxPerDay'] },
  { key: 'partners', keys: ['partner.fotPct', 'partner.taxPct', 'partner.minTravelGapMinutes', 'partner.acceptTimeoutMinutes'] },
  { key: 'sales', keys: ['sales.bonusPerVenue', 'sales.bonusPerAgency', 'sales.bonusAgencyPct', 'sales.bonusAgencyMonths'] },
  { key: 'site', keys: ['relocation.hotelWithPlus', 'relocation.hotelBase', 'relocation.consultation', 'docs.fromPrice', 'docs.socialCardPrice', 'residence.escortFrom', 'residence.stateFee', 'transfer.standardPrice', 'transfer.premiumPrice'] },
  { key: 'other', keys: ['chat.closeAfterHours', 'files.retentionDays', 'consent.version'] },
];

/** Дробные значения (€ с центами, курсы) — в поле можно ввести точку или запятую */
const DECIMAL_KEYS = new Set(['barevPlus.monthlyPriceEur', 'elite.monthlyPriceEur', 'fx.eurAmd', 'fx.eurRub']);

export function Settings() {
  const { t, shared } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const s = useQuery(trpc.bo.settings.get.queryOptions());
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    if (s.data && !draft) setDraft({ ...s.data });
  }, [s.data, draft]);
  const save = useMutation(
    trpc.bo.settings.save.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries();
        toast(t('prv.saved'));
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  if (!draft || !s.data) return <Skeleton h={400} />;
  const changed = Object.keys(draft).filter((k) => draft[k] !== (s.data as Record<string, unknown>)[k]);
  return (
    <>
      <div className="bo-head">
        <h1 className="bo-title">{t('st.title')}</h1>
        <button type="button" className="bo-btn bo-btn--accent" disabled={!changed.length || save.isPending} onClick={() => save.mutate(Object.fromEntries(changed.map((k) => [k, draft[k]])) as never)}>
          {changed.length ? t('st.saveN', { n: changed.length }) : t('ui.save')}
        </button>
      </div>
      <div className="bo-settings">
        {GROUPS.map((g) => (
          <section key={g.key} className="bo-panel">
            <div className="bo-panel__head">
              <h2 className="bo-h2">{t(`st.group.${g.key}`)}</h2>
            </div>
            <div className="bo-col" style={{ gap: 12 }}>
              {g.keys.map((k) => {
                const v = draft[k];
                const label = shared(`admin.settings.${k.replace('.', '_')}`);
                if (DECIMAL_KEYS.has(k))
                  return (
                    <Field key={k} label={label}>
                      <DecimalInput value={Number(v ?? 0)} onChange={(n) => setDraft({ ...draft, [k]: n })} changed={changed.includes(k)} />
                    </Field>
                  );
                return typeof v === 'boolean' ? (
                  <label key={k} className="bo-check">
                    <input type="checkbox" checked={v} onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })} />
                    {label}
                  </label>
                ) : (
                  <Field key={k} label={label}>
                    <input
                      className="bo-input"
                      inputMode={typeof v === 'number' ? 'numeric' : undefined}
                      value={String(v ?? '')}
                      onChange={(e) => setDraft({ ...draft, [k]: typeof v === 'number' ? Number(e.target.value.replace(/\D/g, '')) || 0 : e.target.value })}
                      data-changed={changed.includes(k) || undefined}
                    />
                  </Field>
                );
              })}
            </div>
          </section>
        ))}
        <PaymentMethods />
      </div>
    </>
  );
}

/** Число с дробной частью: держим текст, пока вводят «12,» */
function DecimalInput({ value, onChange, changed }: { value: number; onChange: (n: number) => void; changed: boolean }) {
  const [text, setText] = useState(String(value));
  useEffect(() => {
    if (Number(text.replace(',', '.')) !== value) setText(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      className="bo-input"
      inputMode="decimal"
      value={text}
      onChange={(e) => {
        const t = e.target.value.replace(/[^\d.,]/g, '');
        setText(t);
        const n = Number(t.replace(',', '.'));
        if (Number.isFinite(n)) onChange(n);
      }}
      data-changed={changed || undefined}
    />
  );
}

function PaymentMethods() {
  const { t, locale } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery(trpc.bo.settings.paymentMethods.queryOptions());
  const [edit, setEdit] = useState<{ id?: string; code: string; name: L5; active: boolean } | null>(null);
  const save = useMutation(
    trpc.bo.settings.savePaymentMethod.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: trpc.bo.settings.paymentMethods.queryKey() });
        setEdit(null);
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <h2 className="bo-h2">{t('st.payments')}</h2>
        <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setEdit({ code: '', name: { ru: '', en: '', hy: '', zh: '', es: '' }, active: true })}>
          <I name="plus" size={16} />
        </button>
      </div>
      <ul className="bo-list">
        {(list.data ?? []).map((m) => {
          const name = m.name as L5;
          return (
            <li key={m.id} style={{ opacity: m.active ? 1 : 0.5 }}>
              <span className="bo-grow">{name[locale as keyof L5] || name.ru}</span>
              <button type="button" className="bo-btn bo-btn--ghost bo-btn--sm" onClick={() => setEdit({ id: m.id, code: m.code, name: { ...{ ru: '', en: '', hy: '', zh: '', es: '' }, ...name }, active: m.active })}>
                {t('prv.edit')}
              </button>
            </li>
          );
        })}
      </ul>
      {edit && (
        <Dialog
          open
          onClose={() => setEdit(null)}
          title={t('st.payment')}
          footer={
            <>
              <button type="button" className="bo-btn bo-btn--ghost" onClick={() => setEdit(null)}>
                {t('ui.close')}
              </button>
              <button type="button" className="bo-btn" disabled={!edit.code.trim() || !edit.name.ru.trim() || save.isPending} onClick={() => save.mutate(edit)}>
                {t('ui.save')}
              </button>
            </>
          }
        >
          <Field label={t('st.code')}>
            <input className="bo-input" value={edit.code} maxLength={40} onChange={(e) => setEdit({ ...edit, code: e.target.value.replace(/[^a-z0-9_]/gi, '').toLowerCase() })} placeholder="cash" />
          </Field>
          {LANGS.map((l) => (
            <Field key={l} label={l.toUpperCase()}>
              <input className="bo-input" value={edit.name[l]} maxLength={100} onChange={(e) => setEdit({ ...edit, name: { ...edit.name, [l]: e.target.value } })} />
            </Field>
          ))}
          <label className="bo-check">
            <input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />
            {t('st.paymentActive')}
          </label>
        </Dialog>
      )}
    </section>
  );
}
